import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import {
    HandoffNotice,
    HandoffOutcome,
    WebHandoffSession,
    acceptWebHandoff,
    canCheckHandoffWho,
    clearHandoffNotice,
    clearStashedHandoffCode,
    dropHandedOffSession,
    handoffKeepsSession,
    handoffLabel,
    handoffNoticeHolds,
    hasHandoffWho,
    pendingHandoff,
    readHandoffNotice,
    searchWithoutHandoff,
    settleWebHandoffOnce,
} from "./webHandoff";

export interface HandoffConfirmation {
    /** Who is signed in in this browser now ("" when it cannot say). */
    current: string;
    /** Who the code would sign in. */
    next: string;
    /** Switch to the account of the code. */
    accept: () => void;
    /** Keep the current account; the code is dropped. */
    stay: () => void;
}

/** «Вы вошли как [who]. Не вы? Выйти ×» — over whatever page is open. */
export interface HandoffSignedInNotice {
    who: string;
    /** «×»: the person has seen it. */
    dismiss: () => void;
    /** «Не вы? Выйти»: sign the account the code put here out again. */
    notMe: () => void;
}

export interface WebHandoffUi {
    /**
     * The who of the link is being checked against the person signed in here
     * (a hash, a few ms), or a who without a code is on its way out of the
     * address: nothing is shown — neither the page nor the loader.
     */
    deciding: boolean;
    /** A code is being exchanged: show a loader, decide nothing yet. */
    pending: boolean;
    /** Someone else is signed in here: ask before switching. */
    confirm: HandoffConfirmation | null;
    /** A code seated someone here: say who, on every page, until closed or signed out. */
    notice: HandoffSignedInNotice | null;
}

/**
 * Redeems a web-handoff code of this page (see webHandoff.ts) before the
 * page renders: WebHandoffGate uses it once for the whole site, public pages
 * and RequireAuth's alike, and nothing else may (a code is single use). While
 * it is `pending` the gate shows a loader instead of the page, so RequireAuth
 * does not bounce to /login meanwhile; when another person is signed in in
 * this browser, `confirm` asks first. Afterwards the code is gone (from the
 * address — replace, so Back does not bring it back; the rest of the query,
 * source=app too, and the hash stay — and from the stash of index.html) and
 * the page sees the new session, or, if the code was stale or used, a page
 * that needs a login gets the usual login redirect back to it without the
 * code.
 *
 * A code that seated someone here who was not signed in before (or whom the
 * person agreed to switch to) leaves `notice`: the gate shows «Вы вошли как …
 * Не вы? Выйти» over every page until it is closed, or until that account is
 * no longer the one signed in. Nobody lands in an account silently, whatever
 * page the link led to.
 *
 * Before any of that, the link's who (webHandoff.ts, HANDOFF_WHO_PARAM): when
 * it names the very person signed in here, the code is dropped unspent — no
 * redeem, no loader, no bar; only the address loses the code and the who.
 * While the hash is computed (`deciding`, a few ms) nothing is shown. Any
 * other case redeems as without a who.
 */
export function useWebHandoff(): WebHandoffUi {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, setUser, setToken, logout } = useUser();
    const pending = pendingHandoff(location.pathname, location.search);
    const code = pending?.code ?? null;
    const who = pending?.who ?? null;
    // Only a who whose check can happen at all holds the page (blank) for it.
    const checksWho = code !== null && canCheckHandoffWho(who, user);
    // The code whose who check said «redeem»: from then on the loader.
    const [redeeming, setRedeeming] = useState<string | null>(null);
    // A who with no code to go with (the app never sends one): only taken out.
    const strayWho = code === null && hasHandoffWho(location.pathname, location.search);
    const [asking, setAsking] = useState<{ code: string; session: WebHandoffSession; current: string } | null>(null);
    const [notice, setNotice] = useState<HandoffNotice | null>(() => readHandoffNotice());

    // The address at the moment the code is done with (the setters of the
    // effect and the prompt run later than the render that made them).
    const here = useRef(location);
    here.current = location;
    // A switch agreed to may finish after the person has left the page
    // (Back during the loader): then it must not pull them back to it.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const finish = useCallback(() => {
        clearStashedHandoffCode();
        const { pathname, search, hash } = here.current;
        navigate({ pathname, search: searchWithoutHandoff(search), hash }, { replace: true });
    }, [navigate]);

    useEffect(() => {
        if (!code) return;
        let alive = true;
        const redeem = () =>
            void settleWebHandoffOnce(code, { setUser, setToken }).then((r: HandoffOutcome) => {
                if (r.kind !== "confirm") {
                    // Done with this code even when the person has left the page
                    // meanwhile (Back during the loader): a stash left behind
                    // would bring the loader back on the next page from the app.
                    clearStashedHandoffCode();
                    // settleWebHandoff saved the notice itself; this only shows it.
                    if (r.kind === "signedIn" && r.notice && mounted.current) setNotice(readHandoffNotice());
                }
                if (!alive) return;
                if (r.kind === "confirm") {
                    setAsking({ code, session: r.session, current: r.current });
                    return;
                }
                finish();
            });
        if (!checksWho) {
            // Marked too: the login this redeem makes must not turn the loader
            // into a who check (and a blank page) half-way.
            setRedeeming(code);
            redeem();
        } else {
            void handoffKeepsSession(who, user).then((keep) => {
                if (keep) {
                    // Already signed in here as the person the app names: the
                    // code is dropped unspent, the page shows at once.
                    clearStashedHandoffCode();
                    if (alive) finish();
                    return;
                }
                if (alive) setRedeeming(code);
                redeem();
            });
        }
        return () => {
            alive = false;
        };
        // The code alone decides (its who and the user come with it); the setters are new on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    // A who without a code leaves the address; the page waits the one render it takes.
    useEffect(() => {
        if (!strayWho) return;
        const { pathname, search, hash } = here.current;
        navigate({ pathname, search: searchWithoutHandoff(search), hash }, { replace: true });
    }, [strayWho, navigate]);

    const ask = asking && asking.code === code ? asking : null;
    const confirm: HandoffConfirmation | null = ask
        ? {
              current: ask.current,
              next: handoffLabel(ask.session),
              accept: () => {
                  // The loader stays up (the code is still pending) while the
                  // workers' half of the login finishes, if it has to.
                  setAsking(null);
                  void acceptWebHandoff(ask.session, { setUser, setToken }).then((signedIn) => {
                      if (!mounted.current) {
                          clearStashedHandoffCode();
                          return;
                      }
                      if (signedIn) setNotice(readHandoffNotice());
                      finish();
                  });
              },
              stay: () => {
                  setAsking(null);
                  finish();
              },
          }
        : null;

    // The notice is about the account the code put here: shown while that
    // account is the one signed in. Signed out since (the menu's «Выйти»), or
    // someone else signed in → it goes for good. `user` re-runs this on every
    // login and logout; the keys in storage say whose session it is.
    const holds = notice !== null && handoffNoticeHolds(notice);
    useEffect(() => {
        if (notice && !holds) {
            clearHandoffNotice();
            setNotice(null);
        }
    }, [notice, holds, user]);

    const dismiss = useCallback(() => {
        clearHandoffNotice();
        setNotice(null);
    }, []);
    const notMe = useCallback(() => {
        setNotice(null);
        // The menu's exit first: the activity log sends its last batch with
        // the key about to go. Then what that exit leaves of the session.
        logout();
        dropHandedOffSession();
    }, [logout]);

    const shown: HandoffSignedInNotice | null = notice && holds ? { who: notice.who, dismiss, notMe } : null;

    const deciding = strayWho || (checksWho && redeeming !== code && !confirm);
    return { deciding, pending: code !== null && !confirm && !deciding, confirm, notice: shown };
}
